import { useCallback, useState } from "react"
import {
  Platform,
  ScrollView,
  TextStyle,
  TouchableOpacity,
  useWindowDimensions,
  View,
  ViewStyle,
} from "react-native"

import * as Haptics from "expo-haptics"
import { router, useFocusEffect } from "expo-router"
import * as ScreenOrientation from "expo-screen-orientation"
import { StatusBar } from "expo-status-bar"
import { useSafeAreaInsets } from "react-native-safe-area-context"
import { FeedbackRating } from "types/feedback"
import { TMessage } from "types/transcript"

import { Alert } from "src/components/Alert"
import { BrandBanner } from "src/components/BrandBanner"
import { CloseButton } from "src/components/CloseButton"
import { Button, Text } from "src/components/Ignite"
import { SidekickView } from "src/components/SidekickView"
import { useAuth } from "src/context/AuthContext"
import { useVisemes } from "src/context/VisemeContext"
import { useAppTheme } from "src/hooks/useAppTheme"
import { useStatusBarCompensation } from "src/hooks/useStatusBarCompensation"
import { WEB_CANVAS_MAX_WIDTH } from "src/hooks/useWebLayoutConstraints"
import { useCurrentConversation } from "src/queries/useConversation"
import { useAvailableRoles, useCurrentRole } from "src/queries/useRole"
import { useToDoList } from "src/queries/useToDoList"
import {
  useCleanupThenLogout,
  useClearAllStorageAndLogout,
  useClearAllStorageStatus,
  useResetDemo,
} from "src/queries/useUser"
import { ThemedStyle } from "src/theme"
import { getAppVersion, getNativeBuildNumber, getWebGitSha } from "src/utils/appVersion"
import { openLinkInBrowser } from "src/utils/openLinkInBrowser"
import { backOrToRoot } from "src/utils/routerHelpers"
import { semverString } from "src/utils/stringUtils"

/**
 * FullScreenMenu - A full-screen menu that uses SidekickView for consistent UX.
 *
 * Features:
 * - Avatar overlay with speech integration
 * - Talk/Type/Menu button cluster
 * - Menu options for navigation and settings
 */
export default function FullScreenMenuScreen() {
  const insets = useSafeAreaInsets()
  const { themed, navTheme } = useAppTheme()
  const { apiHost, authHost } = useAuth()
  const { effectiveTopInset } = useStatusBarCompensation()
  const { width, height } = useWindowDimensions()

  // For modals presented from landscape screens:
  // Window dimensions update immediately but safe area insets lag behind
  // Detect when insets haven't caught up with orientation and apply safe overrides
  const isLandscape = width > height
  const insetsAreLandscape = insets.left > 30 || insets.right > 30
  const needsSafeOverrides = Platform.OS !== "web" && (isLandscape || insetsAreLandscape)

  const PORTRAIT_MIN_TOP_INSET = 44
  const safeTopInset = needsSafeOverrides
    ? Math.max(effectiveTopInset, PORTRAIT_MIN_TOP_INSET)
    : insets.top
  const safeLeftInset = needsSafeOverrides ? 0 : insets.left
  const safeRightInset = needsSafeOverrides ? 0 : insets.right

  // On web the header uses position: fixed (see $headerContainer) to escape the
  // LayoutWrapper. Now that the bar has a solid background, constrain it to the
  // centered 720 px canvas column so it doesn't span the whole desktop viewport.
  const isWeb = Platform.OS === "web"
  const webCanvasWidth = Math.min(width, WEB_CANVAS_MAX_WIDTH)
  const webCanvasLeft = (width - webCanvasWidth) / 2
  const headerPositionStyle = isWeb
    ? { top: safeTopInset, left: webCanvasLeft, width: webCanvasWidth }
    : { top: safeTopInset, left: safeLeftInset, right: safeRightInset }

  // The close button is anchored to the window edge (not the canvas edge), so it
  // is positioned independently of the canvas-constrained header background.
  // On web it uses position: fixed to escape the LayoutWrapper and pin to the
  // viewport's right edge, matching the pre-canvas-constraint behavior.
  const closeButtonPositionStyle = {
    position: isWeb ? ("fixed" as any) : ("absolute" as const),
    top: safeTopInset + 22,
    right: safeRightInset + 16,
    zIndex: 2100,
  }

  // Lock to portrait orientation when this screen gains focus
  useFocusEffect(
    useCallback(() => {
      ScreenOrientation.lockAsync(ScreenOrientation.OrientationLock.PORTRAIT_UP).catch(() => {})
    }, []),
  )
  const cleanupThenLogout = useCleanupThenLogout()
  const clearAllStorageAndLogout = useClearAllStorageAndLogout()
  const { isClearingAllStorage } = useClearAllStorageStatus()
  const resetDemoMutation = useResetDemo()
  const { stopSpeech } = useVisemes()

  const currentRole = useCurrentRole()
  const todoListQuery = useToDoList()
  const availableRoles = useAvailableRoles()

  const showPortalButton =
    currentRole === "sponsor_admin" || currentRole === "sme" || currentRole === "testing_member"
  const portalEnv = authHost.split(".")[0]
  const portalUrl =
    portalEnv === "prod"
      ? "https://portal.sidekickplatform.com"
      : `https://${portalEnv}.portal.sidekickplatform.com`
  const { data: currentConversation } = useCurrentConversation()

  // State for secret dev menu (activated by long-pressing version number)
  const [showDevTools, setShowDevTools] = useState(false)

  // Confirm logout before proceeding
  const confirmLogout = useCallback(() => {
    Alert.alert("Log Out", "Are you sure you want to log out?", [
      {
        text: "Cancel",
        style: "cancel",
      },
      {
        text: "Yes, log out",
        style: "default",
        onPress: () => {
          cleanupThenLogout.mutate()
        },
      },
    ])
  }, [cleanupThenLogout])

  // Confirm clearing all storage before proceeding
  const confirmClearAllStorage = useCallback(() => {
    Alert.alert(
      "Clear All Storage",
      "This will delete ALL application data and storage, including settings, conversations, and cache. You will be logged out. This action cannot be undone.",
      [
        {
          text: "Cancel",
          style: "cancel",
        },
        {
          text: "Clear Application Data",
          style: "destructive",
          onPress: () => {
            backOrToRoot()
            clearAllStorageAndLogout.mutate()
          },
        },
      ],
    )
  }, [clearAllStorageAndLogout])

  // Handle reset account history for role
  const handleResetAccountHistory = useCallback(async () => {
    await stopSpeech()
    resetDemoMutation.mutate()
  }, [stopSpeech, resetDemoMutation])

  const handleClose = useCallback(() => {
    backOrToRoot()
  }, [])

  const handleContentLibrary = useCallback(() => {
    router.replace("/content-library" as any)
  }, [])

  const handleFeedbackPress = useCallback(
    (message?: TMessage, rating?: FeedbackRating) => {
      console.log("in onFeedbackPress with message", message)

      // Determine which message to use for feedback
      const feedbackMessage = message?.text
        ? message
        : currentConversation?.transcript?.slice(-1)[0]

      // Navigate to feedback screen with params
      router.replace({
        pathname: "/feedback" as any,
        params: {
          messageId: feedbackMessage?.message_id ?? "",
          messageText: feedbackMessage?.text ?? "",
          rating: rating ?? FeedbackRating.THUMBS_UP,
        },
      })
    },
    [currentConversation],
  )

  return (
    <View style={themed($container)}>
      <StatusBar style={navTheme.dark ? "light" : "dark"} />

      <SidekickView
        showAvatar
        autoShowAvatarOnSpeech
        showCaptions
        showCCToggle
        showButtonCluster
        compactButtonCluster
        darkBackground={true}
        style={themed($container)}
        onMenuPress={handleClose}
        riveOwner="video"
        controlsConfig={{ type: "none" }}
      >
        {/* Custom Header - positioned above the menu content. Background bar is
            constrained to the canvas column on web (see headerPositionStyle). */}
        <View style={[themed($headerContainer), headerPositionStyle]} pointerEvents="box-none">
          <Text style={themed($headerTitle)}>Menu</Text>
        </View>
        {/* Close button anchored to the window edge, independent of the
            canvas-constrained header background. */}
        <CloseButton
          onPress={handleClose}
          accessibilityLabel="Close menu"
          style={closeButtonPositionStyle}
        />
        {/* Menu Content */}
        <ScrollView
          style={themed($scrollView)}
          contentContainerStyle={[
            themed($scrollContent),
            {
              paddingTop: 60, // Space for header (SidekickView provides insets.top)
              paddingBottom: 120, // Space for ButtonCluster
            },
          ]}
        >
          {/* Channel Brand Banner */}
          <View style={themed($channelSection)}>
            <BrandBanner onPress={handleContentLibrary} />
          </View>

          {/* Menu Options */}
          <View style={themed($menuOptions)}>
            <MenuButton title="Settings" onPress={() => router.replace("/settings" as any)} />
            <MenuButton title="Provide Feedback" onPress={() => handleFeedbackPress()} />
            <MenuButton
              title="Your Profile"
              onPress={() => router.replace("/member-profile" as any)}
            />
            {(todoListQuery.data?.length ?? 0) > 0 && (
              <MenuButton title="Content Library" onPress={handleContentLibrary} />
            )}
            {availableRoles.length > 1 && (
              <MenuButton
                title="Change Role"
                onPress={() => router.replace("/change-role" as any)}
              />
            )}
            <MenuButton title="Help" onPress={() => router.replace("/help" as any)} />
            {showPortalButton && (
              <MenuButton title="Open Portal" onPress={() => openLinkInBrowser(portalUrl)} />
            )}
            <MenuButton title="Log Out" onPress={confirmLogout} />
          </View>

          {/* Version Info - Long press to show dev tools */}
          <TouchableOpacity
            style={themed($versionContainer)}
            onLongPress={() => {
              if (Platform.OS !== "web") {
                Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium)
              }
              setShowDevTools(!showDevTools)
            }}
            delayLongPress={5000}
            activeOpacity={0.8}
          >
            <Text style={themed($versionText)}>
              {`Sidekick v${semverString({
                version: getAppVersion(),
                build: apiHost.split(".")[0] !== "prod" ? getNativeBuildNumber() : null,
                sha: apiHost.split(".")[0] !== "prod" ? getWebGitSha() : null,
              })}${apiHost.split(".")[0] !== "prod" ? ` - ${apiHost.split(".")[0]}` : ""}`}
            </Text>
          </TouchableOpacity>

          {/* Secret Dev Tools Menu */}
          {showDevTools && (
            <View style={themed($devToolsContainer)}>
              <View style={themed($devToolsDivider)} />
              <Text style={themed($devToolsTitle)}>Developer Tools</Text>
              <MenuButton
                title="Reset Account History for Role"
                onPress={handleResetAccountHistory}
                disabled={resetDemoMutation.isPending}
              />
              <MenuButton
                title="Clear App Data"
                onPress={confirmClearAllStorage}
                disabled={isClearingAllStorage}
                danger
              />
              <MenuButton
                title="LLM Selection"
                onPress={() => router.replace("/llm-selection" as any)}
              />
              <MenuButton
                title="Crisis Line"
                onPress={() => router.replace("/crisis-line" as any)}
              />
            </View>
          )}
        </ScrollView>
      </SidekickView>
    </View>
  )
}

interface MenuButtonProps {
  title: string
  onPress: () => void
  disabled?: boolean
  danger?: boolean
}

function MenuButton({ title, onPress, disabled, danger }: MenuButtonProps) {
  return (
    <Button
      text={title}
      onPress={onPress}
      preset="subtle"
      disabled={disabled}
      style={[danger && { backgroundColor: "rgba(220, 38, 38, 0.2)" }]}
      textStyle={[danger && { color: "#FF4444" }]}
    />
  )
}

const $container: ThemedStyle<ViewStyle> = ({ colors }) => ({
  flex: 1,
  backgroundColor: colors.background,
})

const $headerContainer: ThemedStyle<ViewStyle> = ({ colors }) => ({
  // On web the app is constrained to a 720 px centred column via LayoutWrapper.
  // position: fixed escapes that container so the header (and its close button)
  // span and anchor to the full viewport width.
  position: Platform.OS === "web" ? ("fixed" as any) : "absolute",
  // Sit below the avatar but above the scrolling menu content so the solid
  // background hides menu text without occluding the avatar.
  //  - Native: the header lives in MediaPlayerShell's content layer while the
  //    avatar lives in a separate foreground layer (zIndex 1000). zIndex only
  //    orders siblings within a parent, so the header is always painted below
  //    the avatar regardless of this value — it just needs to beat the sibling
  //    ScrollView (no zIndex).
  //  - Web: position: fixed escapes into a viewport-level stacking context that
  //    competes globally, so we keep it just under the foreground layer's 1000
  //    to stay below the avatar while remaining above the menu content.
  zIndex: 999,
  flexDirection: "row",
  justifyContent: "center",
  alignItems: "center",
  paddingHorizontal: 16,
  height: 80,
  // Solid background so scrolling menu text doesn't bleed through the header.
  // Safe to use a full-width bar now that the header sits below the avatar's
  // foreground layer (see zIndex note above) — the avatar renders over it.
  backgroundColor: colors.background,
})

const $headerTitle: ThemedStyle<TextStyle> = ({ colors }) => ({
  fontSize: 18,
  fontWeight: "700",
  color: colors.text,
})

const $scrollView: ThemedStyle<ViewStyle> = () => ({
  flex: 1,
})

const $scrollContent: ThemedStyle<ViewStyle> = () => ({
  paddingHorizontal: 24,
})

const $channelSection: ThemedStyle<ViewStyle> = () => ({
  paddingVertical: 24,
})

const $menuOptions: ThemedStyle<ViewStyle> = () => ({
  gap: 8,
})

const $versionContainer: ThemedStyle<ViewStyle> = () => ({
  alignItems: "center",
  marginTop: 24,
})

const $versionText: ThemedStyle<TextStyle> = ({ colors }) => ({
  fontSize: 12,
  textAlign: "center",
  color: colors.textDim,
})

const $devToolsContainer: ThemedStyle<ViewStyle> = () => ({
  marginTop: 16,
  gap: 8,
})

const $devToolsDivider: ThemedStyle<ViewStyle> = () => ({
  height: 1,
  backgroundColor: "rgba(255, 255, 255, 0.1)",
  marginVertical: 16,
})

const $devToolsTitle: ThemedStyle<TextStyle> = ({ colors }) => ({
  fontSize: 14,
  fontWeight: "600",
  textAlign: "center",
  marginBottom: 8,
  color: colors.text,
})
